import React, { useCallback, useEffect, useRef, useMemo } from 'react';
import { Choice, ForwardedChoiceProps } from '../Choice';
import { Input } from './styled';
import { generateId } from '../../utils/generateId';

export type CheckboxValue = string | number;

export interface CheckboxProps extends ForwardedChoiceProps {
  id?: string;
  checked: boolean;
  indeterminate?: boolean;
  value?: CheckboxValue;
  name?: string;
  disabled?: boolean;
  ariaLabel?: string;
  onChange: (checked: boolean, value?: CheckboxValue) => void;
  className?: string;
}

export const Checkbox = ({
  id,
  checked,
  indeterminate,
  value,
  name,
  hint,
  disabled,
  ariaLabel,
  className,
  onChange,
  ...otherProps
}: CheckboxProps) => {
  const componentId = useMemo(() => id ?? generateId('checkbox'), [id]);
  const checkboxRef = useRef<HTMLInputElement>(null);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.currentTarget.checked, value);
    },
    [onChange, value],
  );

  useEffect(() => {
    if (!checkboxRef.current) return;
    if (indeterminate === true) {
      checkboxRef.current.indeterminate = true;
      return;
    }
    checkboxRef.current.indeterminate = false;
  }, [indeterminate]);

  const ariaDescribedBy = hint ? `hint-${componentId}` : undefined;

  return (
    <Choice
      htmlFor={componentId}
      disabled={disabled}
      className={className}
      hint={hint}
      {...otherProps}
    >
      <Input
        ref={checkboxRef}
        id={componentId}
        type="checkbox"
        checked={checked}
        value={value}
        name={name}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        onChange={handleChange}
      />
    </Choice>
  );
};
